import React, { useEffect, useState } from 'react';
import { Button, Typography, Row, Col, Menu } from 'antd';
import { Link, useHistory, useParams } from 'react-router-dom';
import {
  getAlgorithmValue,
  getAlgorithmIcon,
  isAlgorithmEnabled,
  isAlgorithmDisabledForCustomization,
  isAlgorithmDisabledForFlow,
} from '../../lib/util/algorithm';
import { slugify } from '../../lib/util';
import { getProjects, getClients } from '../../lib/api/aws-api';
import { getAlgorithms } from '../../lib/api/algorithm-api';
import { withRouter } from 'react-router-dom';
import { useAuthenticationContext } from '../authentication/AuthenticationContext';
import { Algorithm, Project, Client } from '../../types';
import { CONFIG } from '../../config/Config';
import { SideBarTitle } from '../../components/text/Titles';
import { SidebarSider } from '../../components/layout/CustomSider';
import { MenuSkeleton } from '../../components/loading/Placeholder';
import { Icon } from '@ant-design/compatible';

const { Text } = Typography;

type Props = {
  location: { pathname: string };
};

const SideBar = (props: Props) => {
  let history = useHistory();

  const { algorithmId, projectId, clientName } = useParams<{
    algorithmId: string;
    projectId: string;
    clientName: string;
  }>();

  const [algorithms, setAlgorithms] = useState<Algorithm[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [inboxClients, setInboxClients] = useState<Client[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [showOverview, setShowOverview] = useState<boolean>(false);
  const [showDatasets, setShowDatasets] = useState<boolean>(false);

  const { currentUser, logOut } = useAuthenticationContext();

  useEffect(() => {
    const loadAlgorithms = async () => {
      setShowOverview(CONFIG.overview.enabled);
      setShowDatasets(CONFIG.datasets.enabled);
      setLoading(true);
      try {
        const algorithms = await getAlgorithms();
        const projects = await getProjects();
        const clients = await getClients();
        const configuredAlgorithms = CONFIG.algorithms;
        setProjects(projects);
        setInboxClients(clients);
        setAlgorithms(
          configuredAlgorithms
            .map(it => algorithms.find(cit => `${cit.id}` === `${it.id}`))
            .filter((algorithm): algorithm is Algorithm => !!algorithm)
        );
        setLoading(false);
        setError(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
        setError(true);
      }
    };

    loadAlgorithms();
  }, []);

  useEffect(() => {
    return () => {};
  });

  return (
    <SidebarSider width={300}>
      <Row className="flex-col justify-between h-full w-full flex-nowrap">
        <Col className="w-full h-full overflow-y-auto">
          <Row className="flex-col" style={{ borderRight: '1px solid #e8e8e8' }}>
            <Col className="px-6 py-10">
              <img
                src={require('../../images/dokapi-logo-dark-green-fern.svg')}
                alt="Dokapi"
                style={{ width: '120px' }}
              />
            </Col>

            {error && (
              <Row className="w-full flex-col items-center justify-center p-4">
                <Icon className="text-xl" type="exclamation-circle" />
                <Text className="text-center">
                  Something went wrong. Check your internet connection or try again later.
                </Text>
              </Row>
            )}

            {!error && (
              <>
                <Col className="pt-6">
                  <SideBarTitle>ABOUT</SideBarTitle>
                  {loading && <MenuSkeleton count={CONFIG.overview.enabled ? 2 : 1} />}
                  {!loading && (
                    <Menu
                      mode="inline"
                      selectedKeys={
                        props.location.pathname && props.location.pathname === '/'
                          ? ['introduction']
                          : []
                          ? props.location.pathname && props.location.pathname.startsWith('/overview')
                            ? ['overview']
                            : []
                          : []
                      }>
                      <Menu.Item key={'introduction'}>
                        <Link className="text" to="/">
                          <Icon type="read" />
                          <span className="ml-1">Introduction</span>
                        </Link>
                      </Menu.Item>
                      {showOverview && !loading && (
                        <Menu.Item key={'overview'}>
                          <Link className="text" to="/overview">
                            <Icon type="appstore" />
                            <span className="ml-1">Overview</span>
                          </Link>
                        </Menu.Item>
                      )}
                    </Menu>
                  )}
                </Col>

                {loading && <MenuSkeleton count={CONFIG.algorithms.filter(algorithm => algorithm.enabled).length} />}
                {!loading && algorithms.filter(algorithm => isAlgorithmEnabled(algorithm.id)).length !== 0 && (
                  <Col className="pt-6">
                    <SideBarTitle>DEMO</SideBarTitle>
                    <Menu
                      mode="inline"
                      selectedKeys={
                        props.location.pathname && props.location.pathname.startsWith('/demo')
                          ? [`demo-${algorithmId}`]
                          : []
                      }>
                      {algorithms
                        .filter(algorithm => isAlgorithmEnabled(algorithm.id))
                        .map(algorithm => {
                          return (
                            <Menu.Item key={`demo-${algorithm.id}`}>
                              <Link className="text" to={`/demo/${algorithm.id}/models`}>
                                <Icon type={getAlgorithmIcon(algorithm.name) || undefined} />
                                <span className="ml-1">{getAlgorithmValue(algorithm.name)}</span>
                              </Link>
                            </Menu.Item>
                          );
                        })}
                    </Menu>
                  </Col>
                )}

                {loading && (
                  <MenuSkeleton
                    count={
                      CONFIG.algorithms.filter(algorithm => algorithm.enabled && !algorithm.hide_customization).length
                    }
                  />
                )}
                {algorithms.filter(algorithm => !isAlgorithmDisabledForCustomization(algorithm.id)).length !== 0 && (
                  <Col className="pt-6">
                    <SideBarTitle>CUSTOMISATION</SideBarTitle>
                    <Menu
                      mode="inline"
                      selectedKeys={
                        props.location.pathname && props.location.pathname.startsWith('/custom')
                          ? [`custom-${algorithmId}`]
                          : []
                      }>
                      {algorithms
                        .filter(algorithm => !isAlgorithmDisabledForCustomization(algorithm.id))
                        .map(algorithm => {
                          return (
                            <Menu.Item key={`custom-${algorithm.id}`}>
                              <Link className="text" to={`/custom/${algorithm.id}/models`}>
                                <Icon type={getAlgorithmIcon(algorithm.name) || undefined} />
                                <span className="ml-1">{getAlgorithmValue(algorithm.name)}</span>
                              </Link>
                            </Menu.Item>
                          );
                        })}
                    </Menu>
                  </Col>
                )}

                {loading && <MenuSkeleton count={CONFIG.algorithms.filter(algorithm => algorithm.enabled).length} />}
                {algorithms.filter(algorithm => !isAlgorithmDisabledForFlow(algorithm.id)).length !== 0 && (
                  <Col className="pt-6">
                    <SideBarTitle>END TO END</SideBarTitle>
                    <Menu
                      mode="inline"
                      selectedKeys={
                        props.location.pathname && props.location.pathname.startsWith('/flows')
                          ? [`flow-${algorithmId}`]
                          : []
                      }>
                      {algorithms
                        .filter(algorithm => !isAlgorithmDisabledForFlow(algorithm.id))
                        .map(algorithm => {
                          return (
                            <Menu.Item key={`flow-${algorithm.id}`}>
                              <Link className="text" to={`/flows/${algorithm.id}`}>
                                <Icon type={getAlgorithmIcon(algorithm.name) || undefined} />
                                <span className="ml-1">{getAlgorithmValue(algorithm.name)}</span>
                              </Link>
                            </Menu.Item>
                          );
                        })}
                    </Menu>
                  </Col>
                )}

                {showDatasets && projects.length ? (
                  <Col className="pt-6">
                    <SideBarTitle>DATASETS</SideBarTitle>
                    {loading && <MenuSkeleton count={2} />}
                    <Menu
                      mode="inline"
                      selectedKeys={
                        props.location.pathname && props.location.pathname.startsWith('/projects')
                          ? [`project-${projectId}`]
                          : []
                      }>
                      {projects.map(project => {
                        return (
                          <Menu.Item key={`project-${project.id}`}>
                            <Link className="text" to={`/projects/${project.id}/datasets`}>
                              <Icon type="file-search" />
                              <span className="ml-1">{project.name}</span>
                            </Link>
                          </Menu.Item>
                        );
                      })}
                    </Menu>
                  </Col>
                ) : (
                  <></>
                )}

                {inboxClients.length ? (
                  <Col className="pt-6">
                    <SideBarTitle>INBOX</SideBarTitle>
                    {loading && <MenuSkeleton count={2} />}
                    <Menu
                      mode="inline"
                      selectedKeys={
                        props.location.pathname && props.location.pathname.startsWith('/inbox')
                          ? [`client-${clientName}`]
                          : []
                      }>
                      {inboxClients.map((client: any) => {
                        return (
                          <Menu.Item key={`client-${slugify(client.io_customer_name)}`}>
                            <Link className="text" to={`/inbox/${slugify(client.io_customer_name)}`}>
                              <Icon type="file-search" />
                              <span className="ml-1">{client.io_customer_name}</span>
                            </Link>
                          </Menu.Item>
                        );
                      })}
                    </Menu>
                  </Col>
                ) : (
                  <></>
                )}
              </>
            )}
          </Row>
        </Col>

        {currentUser && (
          <Col className="border-0 border-t border-solid border-gray-200 px-6 py-4 min-h-inherit">
            <Text>{currentUser.username}</Text>
            <br />
            <Button
              type="link"
              className="p-0"
              onClick={() => {
                history.push('/');
                logOut();
              }}>
              Log out
            </Button>
            <span className="mx-2">|</span>
            <Button type="link" className="p-0" onClick={() => history.push('/admin')}>
              Admin
            </Button>
          </Col>
        )}
      </Row>
    </SidebarSider>
  );
};

export default withRouter(SideBar);
